import {
  createElement,
  type CSSProperties,
  type ReactElement,
  type Ref,
  type SVGProps,
} from "react";

import type { LucideIconData } from "@lucide/icons";
import { buildLucideIconNode } from "@lucide/icons/build";

import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";

import { BrandIconData } from "../brand-icon/BrandIcon";
import type { UISize } from "../utils/UIContext";
import { type UiColor } from "../utils/colors";
import { ICON_SIZE_PX } from "../utils/iconSize";
import { type UiPaletteKey, resolveUiPalette } from "../utils/uiPalette";

type LucideIconNode = ReturnType<typeof buildLucideIconNode>;

export type IconSize = UISize;

export { ICON_SIZE_PX };

export type IconProps = {
  /** Lucide icon data from `@lucide/icons` (e.g. `LightbulbIcon`). */
  icon?: LucideIconData | BrandIconData | null;
  color?: UiColor;
  size?: IconSize;
  className?: string;
  style?: CSSProperties;
  ref?: Ref<SVGSVGElement>;
} & Omit<SVGProps<SVGSVGElement>, "color" | "children" | "ref">;

function toReactAttrs(
  attrs: Record<string, string>,
): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  for (const [key, value] of Object.entries(attrs)) {
    if (key === "key") continue;
    if (key === "class") {
      out.className = value;
      continue;
    }
    out[key.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())] = value;
  }
  return out;
}

function nodeToElement(
  node: LucideIconNode,
  key?: string | number,
): ReactElement {
  const [tag, attrs, children] = node;
  return createElement(
    tag,
    { key: attrs.key ?? key, ...toReactAttrs(attrs) },
    children?.map((child, index) => nodeToElement(child, index)),
  );
}

const IconComponent = ({
  icon,
  color,
  size = "medium",
  className,
  style,
  ref,
  ...rest
}: IconProps) => {
  const theme = useTheme();
  if (!icon) {
    return null;
  }
  const uiPalette = color
    ? resolveUiPalette(theme, color as UiPaletteKey)
    : undefined;
  const iconColor = uiPalette?.fg || "currentColor";

  const px = ICON_SIZE_PX[size];
  const cname = cx(className);
  if (!icon.node) {
    const BrandIcon = icon;
    return (
      <BrandIcon
        size={px}
        color={iconColor}
        className={cname}
        style={style}
        ref={ref}
        {...rest}
      />
    );
  }

  const [, attrs, children] = buildLucideIconNode(icon, {
    size: px,
    strokeWidth: 2.5,
    color: iconColor,
    className: cx(cname),
  });

  const svgAttrs = toReactAttrs(attrs);
  // CSS uses `stroke: currentColor`; set `color` so named colours
  // (e.g. green) win over the Lucide stroke presentation attribute.
  const colorStyle = iconColor !== "currentColor" ? { color: iconColor } : {};

  return (
    <svg
      {...svgAttrs}
      {...rest}
      ref={ref}
      className={svgAttrs.className}

      style={{ ...colorStyle, ...style }}
    >
      {children?.map((child, index) => nodeToElement(child, index))}
    </svg>
  );
};

export const Icon = styled(IconComponent)`
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
  fill: none;
  stroke: currentColor;
  alignment-baseline: central;
  overflow: visible;

  @keyframes sdo-icon-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
